import axios from '../axiosInstance'
import constants from '../../constants'
import { isAuthenticated } from '../../utils/authSession'
import { Builder, BuilderListResult, BuilderResult, BuilderType } from './types'

const buildersUrl = (appId: string) => `${constants.customerDomain}apps/${appId}/vcb/builders`

/** Handled API errors resolve as their payload; only a response-less failure rejects. */
const handled = (error: { response?: { data?: unknown } }) =>
  error?.response?.data ?? Promise.reject(error)

/**
 * Lists an app's builders, optionally filtered server-side by `type`.
 * Returns `false` when no bearer token is stored, so the call was never made.
 */
export async function list(app: { id: string }, type?: BuilderType): Promise<BuilderListResult> {
  if (!isAuthenticated()) return false

  const query = type ? `?type=${type}` : ''
  return axios
    .get(`${buildersUrl(app.id)}${query}`)
    .then(res => res.data)
    .catch(handled)
}

export async function get(id: string, app: { id: string }): Promise<BuilderResult> {
  if (!isAuthenticated()) return false

  return axios
    .get(`${buildersUrl(app.id)}/${id}`)
    .then(res => res.data)
    .catch(handled)
}

export async function add(
  post: Record<string, unknown>,
  app: { id: string },
): Promise<BuilderResult> {
  if (!isAuthenticated()) return false

  return axios
    .post(buildersUrl(app.id), post)
    .then(res => {
      localStorage.removeItem('ccPreviewBuilderId')
      return res.data
    })
    .catch(handled)
}

export async function update(
  id: string,
  post: Record<string, unknown>,
  app: { id: string },
): Promise<BuilderResult> {
  if (!isAuthenticated()) return false

  return axios
    .put(`${buildersUrl(app.id)}/${id}`, post)
    .then(res => res.data)
    .catch(handled)
}

/** Clears a builder's settings back to the template defaults. */
export async function reset(id: string, app: { id: string }): Promise<BuilderResult> {
  if (!isAuthenticated()) return false

  return axios
    .delete(`${buildersUrl(app.id)}/${id}/settings`, {
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json;charset=UTF-8',
      },
    })
    .then(res => res.data)
    .catch(handled)
}

/** Public endpoint used by the preview flow — unauthenticated, and throws rather than resolving. */
export async function getTemporaryBuilder(builderId: string): Promise<Builder> {
  if (!builderId) throw new Error('builderId is required.')

  const response = await axios.get(`${constants.chatBuilderPublicApiUrl}/builders/${builderId}`)
  return response.data.data
}
